import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { ConsentContext } from './ConsentContext';
import {
  DEFAULT_WIDGET_CONSENT_STATE,
  normalizeWidgetConsentState,
  updateWidgetConsentStateAttribute,
  type WidgetConsentState,
} from './consentState';
import {
  clearStoredConsentState,
  readStoredConsentState,
  writeStoredConsentState,
} from './consentStorage';

type ConsentUpdateSource = 'initial' | 'integrator' | 'widget';

interface ConsentProviderProps {
  hostElement?: HTMLElement | null;
  consentState?: WidgetConsentState;
  children: ComponentChildren;
}

interface ConsentSnapshot {
  value: WidgetConsentState;
  source: ConsentUpdateSource;
}

function createConsentSnapshot(
  value: WidgetConsentState,
  source: ConsentUpdateSource,
): ConsentSnapshot {
  return { value, source };
}

export function ConsentProvider({
  hostElement,
  consentState = DEFAULT_WIDGET_CONSENT_STATE,
  children,
}: ConsentProviderProps) {
  const normalizedConsentState = normalizeWidgetConsentState(consentState);
  const [snapshot, setSnapshot] = useState(() => {
    // When the integrator hasn't made an explicit choice (prompt = default),
    // restore from localStorage so the user's previous decision survives page reloads.
    const initial =
      normalizedConsentState === 'prompt'
        ? (readStoredConsentState() ?? normalizedConsentState)
        : normalizedConsentState;
    return createConsentSnapshot(initial, 'initial');
  });
  const lastExternalConsentStateRef = useRef(normalizedConsentState);

  useEffect(() => {
    if (normalizedConsentState === lastExternalConsentStateRef.current) {
      return;
    }

    lastExternalConsentStateRef.current = normalizedConsentState;

    // Integrator explicitly changed consent — their value takes precedence.
    // If they reset to prompt, clear localStorage so we don't immediately
    // restore the user's previous choice against their intent.
    if (normalizedConsentState === 'prompt') {
      clearStoredConsentState();
    }

    setSnapshot((currentSnapshot) => {
      if (currentSnapshot.value === normalizedConsentState) {
        return currentSnapshot;
      }

      return createConsentSnapshot(normalizedConsentState, 'integrator');
    });
  }, [normalizedConsentState]);

  const value = useMemo(
    () => ({
      consentState: snapshot.value,
      setConsentState: (nextConsentState: WidgetConsentState) => {
        lastExternalConsentStateRef.current = nextConsentState;
        updateWidgetConsentStateAttribute(hostElement, nextConsentState);
        writeStoredConsentState(nextConsentState);

        setSnapshot((currentSnapshot) => {
          if (currentSnapshot.value === nextConsentState) {
            return currentSnapshot;
          }

          return createConsentSnapshot(nextConsentState, 'widget');
        });
      },
    }),
    [hostElement, snapshot.value],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}
